import { useRef, useCallback, useEffect } from 'react';
import { trackListingView } from '@/lib/analytics';

/**
 * Shared IntersectionObserver for mobile impression tracking.
 * Instead of 69 individual observers, one observer tracks all cards.
 * Cards register via the returned ref callback.
 */
export function useBatchImpressionTracker() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const trackedRef = useRef<Set<string>>(new Set());
  const elementMapRef = useRef<Map<Element, string>>(new Map());

  // Create observer lazily
  const getObserver = useCallback(() => {
    if (observerRef.current) return observerRef.current;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const listingId = elementMapRef.current.get(entry.target);
          if (listingId && !trackedRef.current.has(listingId)) {
            trackedRef.current.add(listingId);
            trackListingView(listingId);
            // Stop observing this element
            observerRef.current?.unobserve(entry.target);
            elementMapRef.current.delete(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    return observerRef.current;
  }, []);

  // Register a card element for tracking
  const observe = useCallback(
    (element: HTMLElement | null, listingId: string) => {
      if (!element || !listingId) return;
      if (trackedRef.current.has(listingId)) return; // Already tracked this session

      const observer = getObserver();
      elementMapRef.current.set(element, listingId);
      observer.observe(element);
    },
    [getObserver]
  );

  // Unregister a card element
  const unobserve = useCallback((element: HTMLElement | null) => {
    if (!element) return;
    observerRef.current?.unobserve(element);
    elementMapRef.current.delete(element);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      elementMapRef.current.clear();
    };
  }, []);

  return { observe, unobserve };
}
